import React, { Component, ReactNode } from 'react';
import { handleError, shouldIgnoreError } from '@/lib/error-monitoring';
import { DRXError } from '@/lib/drx-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  private recoveryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Don't show error UI for auth context errors or ignored errors
    if (shouldIgnoreError(error.message) ||
        error.message?.includes('must be used within a') ||
        error.message?.includes('FirebaseAuthProvider')) {
      return { hasError: false };
    }
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidMount() {
    // Auto-recover from error state after 3 seconds for transient errors
    if (this.state.hasError && this.state.error?.message) {
      const isTransientError =
        shouldIgnoreError(this.state.error.message) ||
        this.state.error.message.includes('must be used within a') ||
        this.state.error.message.includes('FirebaseAuthProvider');

      if (isTransientError) {
        this.recoveryTimeout = setTimeout(() => {
          console.log('🔄 Auto-recovering from transient error');
          this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        }, 3000);
      }
    }
  }

  componentWillUnmount() {
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Don't handle browser extension errors
    if (shouldIgnoreError(error.message)) {
      console.warn('🔌 Ignored browser extension error in ErrorBoundary:', error.message);
      return;
    }

    // Don't handle auth context errors - they should be handled by the auth system
    if (error.message?.includes('must be used within a') || error.message?.includes('FirebaseAuthProvider')) {
      console.warn('🔐 Ignored auth context error in ErrorBoundary:', error.message);
      // Reset the error boundary state so it doesn't show the error UI
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
      return;
    }

    this.setState({ error, errorInfo });

    const boundaryError = new DRXError({
      code: 'REACT_ERROR_BOUNDARY',
      devMessage: error.message,
      userMessage: 'We hit an unexpected issue while rendering this screen.',
      cause: error,
      level: 'error',
      tags: { source: 'errorBoundary' },
      context: {
        componentStack: errorInfo.componentStack,
      },
    });
    handleError(boundaryError, 'error-boundary');

    // Log additional error info
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-xl text-gray-900">
                Oops! Something went wrong
              </CardTitle>
              <CardDescription>
                We're sorry, but something unexpected happened. This has been logged and we'll look into it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <Button 
                  onClick={this.handleRetry}
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
              
              <div className="text-center">
                <Button 
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  className="w-full"
                >
                  Go to Homepage
                </Button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                    Error Details (Development)
                  </summary>
                  <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-40">
                    <div className="text-red-600 font-bold mb-2">
                      {this.state.error.name}: {this.state.error.message}
                    </div>
                    <div className="text-gray-700">
                      {this.state.error.stack}
                    </div>
                    {this.state.errorInfo && (
                      <div className="mt-2 pt-2 border-t border-gray-300">
                        <div className="text-blue-600 font-bold mb-1">Component Stack:</div>
                        <div className="text-gray-700">
                          {this.state.errorInfo.componentStack}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

// Hook for handling async errors in functional components
export function useErrorHandler() {
  return (error: Error, context?: string) => {
    if (shouldIgnoreError(error.message)) {
      return;
    }
    handleError(error, context);
  };
}
